// What the merchant changed, shown before the customer approves and pays.
//
// The handoff requires the quote to show "every change the merchant made to
// the cart (quantity, price, removed items) before the customer pays". Asking
// someone to approve a total without telling them a $3 item became $5, or that
// two things are missing, is exactly where trust is lost — and it is the most
// common reason a dispute gets opened later.
//
// Renders nothing when the quote matches the cart, so an honest quote adds no
// noise.

import { ArrowRight, MinusCircle, PlusCircle, TrendingUp } from 'lucide-react';
import { diffQuote, type QuoteLineChange } from '../../lib/quoteDiff';
import type { QuoteItem, RequestItem } from '../../lib/api';

const htg = (minor: number) => `HTG ${(minor / 100).toLocaleString()}`;

/** Trim trailing zeros so "2.00" reads as "2". */
const qty = (n: number) => (Number.isInteger(n) ? String(n) : String(n));

function Line({ c }: { c: QuoteLineChange }) {
  if (c.kind === 'removed') {
    return (
      <li className="flex items-start gap-2 py-1.5">
        <MinusCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-500" aria-hidden="true" />
        <span className="text-[12.5px] text-slate-700">
          <span className="font-semibold">{c.name}</span>
          {' — '}
          {/* Named explicitly: "not available" is a fact the customer may want
              to act on, not a silent omission. */}
          <span className="text-rose-600 font-semibold">not available</span>
          <span className="text-slate-400"> (was {qty(c.before!.quantity)} × {htg(c.before!.unitHtgMinor)})</span>
        </span>
      </li>
    );
  }

  if (c.kind === 'added') {
    return (
      <li className="flex items-start gap-2 py-1.5">
        <PlusCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
        <span className="text-[12.5px] text-slate-700">
          <span className="font-semibold">{c.name}</span>
          {' — '}
          <span className="text-emerald-700 font-semibold">added</span>
          <span className="text-slate-400"> ({qty(c.after!.quantity)} × {htg(c.after!.unitHtgMinor)})</span>
        </span>
      </li>
    );
  }

  const priceMoved = c.before!.unitHtgMinor !== c.after!.unitHtgMinor;
  const qtyMoved = c.before!.quantity !== c.after!.quantity;

  return (
    <li className="flex items-start gap-2 py-1.5">
      <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
      <span className="text-[12.5px] text-slate-700">
        <span className="font-semibold">{c.name}</span>
        {qtyMoved && (
          <span className="text-slate-500">
            {' — qty '}
            <span className="tabular-nums">{qty(c.before!.quantity)}</span>
            <ArrowRight className="inline w-3 h-3 mx-0.5" aria-label="changed to" />
            <span className="tabular-nums font-semibold text-slate-800">{qty(c.after!.quantity)}</span>
          </span>
        )}
        {priceMoved && (
          <span className="text-slate-500">
            {qtyMoved ? ', ' : ' — '}
            {'unit '}
            <span className="tabular-nums">{htg(c.before!.unitHtgMinor)}</span>
            <ArrowRight className="inline w-3 h-3 mx-0.5" aria-label="changed to" />
            <span className="tabular-nums font-semibold text-slate-800">{htg(c.after!.unitHtgMinor)}</span>
          </span>
        )}
      </span>
    </li>
  );
}

export function QuoteChanges({
  requestItems,
  quoteItems,
}: {
  requestItems: RequestItem[];
  quoteItems: QuoteItem[];
}) {
  const diff = diffQuote(requestItems ?? [], quoteItems ?? []);

  // An unchanged quote needs no explanation.
  if (!diff.hasChanges) return null;

  const parts = [
    diff.removedCount && `${diff.removedCount} unavailable`,
    diff.repricedCount && `${diff.repricedCount} repriced`,
    diff.addedCount && `${diff.addedCount} added`,
  ].filter(Boolean);

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
        What the merchant changed
      </p>
      <p className="mt-0.5 text-[11.5px] text-amber-900/80">
        {parts.join(' · ')} — check before you approve.
      </p>
      <ul className="mt-1.5 divide-y divide-amber-200/60">
        {diff.changed.map((c, i) => <Line key={`${c.kind}-${c.name}-${i}`} c={c} />)}
      </ul>
    </div>
  );
}
